import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Rocket, ArrowLeft } from "lucide-react";
import Stats from "../components/nasa/Stats";
import ProgramFeatures from "../components/nasa/ProgramFeatures";
import ProgramActivities from "../components/nasa/ProgramActivities";
import NasaTrainingCustomized from "../components/nasa/NasaTrainingCustomized";
import FAQ from "../components/nasa/FAQ";
import CTA from "../components/nasa/CTA";
import apiClient from "../services/api";
import { Program } from "../types/program";

const NasaCustomized: React.FC = () => {
    const [customizedProgram, setCustomizedProgram] = useState<Program | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchProgram = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await apiClient.getProgram("nasa-space-training-customized");

                if (response.success) {
                    const programData = response.data.program || response.data;
                    setCustomizedProgram(programData);
                } else {
                    setError("Customized program not found");
                }
            } catch (err: any) {
                console.error("Error fetching customized program:", err);
                setError("Failed to load customized program details");
            } finally {
                setLoading(false);
            }
        };

        fetchProgram();
    }, []);

    const handleScrollToTraining = () => {
        const trainingSection = document.getElementById("training-section");
        if (trainingSection) {
            trainingSection.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading program details....</p>
                </div>
            </div>
        );
    }

    if (error || !customizedProgram) {
        return (
            <div className="min-h-screen bg-gray-50 py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <h1 className="text-4xl font-bold text-gray-900 mb-4">Program Not Found</h1>
                        <p className="text-gray-600 mb-8">{error || "The program you are looking for does not exist."}</p>
                        <Link to="/programs" className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors">
                            <ArrowLeft className="h-5 w-5 mr-2" />
                            Back to Programs
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-black">
            {/* Hero Section */}
            <div className="relative py-12 md:py-16 lg:py-20 flex items-center justify-center overflow-hidden">
                {/* Background Video */}
                <div className="absolute inset-0">
                    <video className="w-full h-full object-cover" autoPlay muted loop playsInline>
                        <source src="/videos/nasa.mp4" type="video/mp4" />
                    </video>
                    <div className="absolute inset-0 bg-black/30"></div>
                </div>

                {/* Content */}
                <div className="relative z-10 text-center max-w-6xl mx-auto px-4">
                    <motion.div
                        className="w-auto inline-flex items-center px-8 py-2 bg-[#F9731633] backdrop-blur-md rounded-full text-[#FB923C] text-sm font-medium mb-8 shadow-2xl border border-[#FB923C4D]"
                        whileHover={{ scale: 1.05 }}
                        transition={{ type: "spring", stiffness: 300 }}
                    >
                        <Rocket className="w-5 h-5 mr-3 text-[#FB923C]" />
                        <span className="text-[#FB923C] text-xs md:text-sm lg:text-md">NASA CUSTOMIZED PROGRAM</span>
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="text-4xl md:text-6xl lg:text-8xl font-black text-white mb-8 leading-tight"
                    >
                        NASA CUSTOMIZED
                        <span className="block bg-gradient-to-r from-orange-400 via-red-500 to-pink-500 bg-clip-text text-transparent">SPACE PROGRAMS</span>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                        className="text-xs md:text-sm lg:text-lg text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed"
                    >
                        Join NASA's customized, groundbreaking mission to uncover signs of life beyond Earth. This program combines cutting-edge astrobiology research with hands-on space technology development.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.6 }}
                        className="flex flex-col sm:flex-row gap-6 justify-center items-center"
                    >
                        <button
                            onClick={handleScrollToTraining}
                            className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-4 rounded-xl font-black text-sm md:text-md hover:from-orange-600 hover:to-red-600 transition-all duration-300 shadow-2xl flex items-center"
                        >
                            <Rocket className="mr-3 h-6 w-6" />
                            ENROLL NOW
                        </button>
                        <Link
                            to="/contact"
                            className="border border-white text-white px-8 py-4 rounded-xl font-bold text-sm md:text-md hover:bg-white hover:text-black transition-all duration-300 flex items-center"
                        >
                            CONTACT US
                        </Link>
                    </motion.div>
                </div>
            </div>

            {/* Stats Section */}
            <div id="program-details">
                <Stats />
            </div>

            {/* Program Features */}
            <ProgramFeatures />

            {/* Program Activities Section */}
            <ProgramActivities />

            {/* Training Section */}
            <NasaTrainingCustomized customizedProgram={customizedProgram} />

            {/* FAQ */}
            <FAQ program8Days={customizedProgram} nasaProgram={customizedProgram} hideBrochureButtons />

            {/* CTA Section */}
            <CTA nasaProgram={customizedProgram} />
        </div>
    );
};

export default NasaCustomized;
